import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { intToTimeStr, buildLocalDate } from '@/helpers/dateTime';
import { isLocaleEnUs, isUsDateFormat } from '@/helpers/utility';

const DATE_FORMATS = {
  us: {
    dateSlash: 'MM/dd/yyyy',
    dateDash: 'MM-dd-yyyy',
    // Year-less variant, for dense axis labels where the year is redundant
    // because the surrounding range already states it.
    dateShortSlash: 'M/d',
    dateShortDash: 'M-d',
    dateTimeSlash: 'MM/dd/yyyy hh:mm a',
    dateTimeDash: 'MM-dd-yyyy hh:mm a',
    time: 'hh:mm a',
  },
  tr: {
    dateSlash: 'dd/MM/yyyy',
    dateDash: 'dd-MM-yyyy',
    dateShortSlash: 'd/M',
    dateShortDash: 'd-M',
    dateTimeSlash: 'dd/MM/yyyy HH:mm',
    dateTimeDash: 'dd-MM-yyyy HH:mm',
    time: 'HH:mm',
  },
};

// Maps a requested format type onto its key prefix in DATE_FORMATS.
const TYPE_KEYS = {
  dateTime: 'dateTime',
  dateShort: 'dateShort',
};

/**
 * Returns date / time format based on locale.
 *
 * @param {Object} [options]
 * @param {'date' | 'time' | 'dateTime' | 'dateShort'} [options.type] - Format type
 *   ('dateShort' omits the year, e.g. '7/21' / '21/7')
 * @param {'slash' | 'dash'} [options.separator] - Date separator type
 * @param {boolean} [options.us] - Force the US / non-US table instead of
 *   deriving it from the company's unit setting
 *
 * @returns {string} Format string
 */
export function getLocalFormat({ type = 'date', separator = 'slash', us } = {}) {
  const localeKey = (us === undefined ? isUsDateFormat() : us) ? 'us' : 'tr';

  if (type === 'time') {
    return DATE_FORMATS[localeKey].time;
  }

  const typeKey = TYPE_KEYS[type] || 'date';
  const sepKey = separator === 'dash' ? 'Dash' : 'Slash';

  return DATE_FORMATS[localeKey][`${typeKey}${sepKey}`];
}

/**
 * Like `getLocalFormat`, but keyed on the browser locale alone rather than the
 * company's unit setting.
 *
 * Use this for surfaces that must follow the language the admin is actually
 * reading — e.g. an English-browser admin expects '7/21' and '8:00 A' even at a
 * metric company (VSER-13679). Prefer plain `getLocalFormat` everywhere else,
 * so grids and forms stay consistent with the company's configured units.
 *
 * @param {Object} [options] - Same options as `getLocalFormat`, minus `us`
 * @returns {string} Format string
 */
export function getBrowserLocaleFormat(options = {}) {
  return getLocalFormat({ ...options, us: isLocaleEnUs() });
}

/**
 * Detects the input value type used for date / time formatting.
 *
 * @param {*} value - Any value that may represent a date or time input.
 *
 * @returns {'empty' | 'date' | 'timestamp' | 'intDate' | 'intTime' | 'isoDate' | 'timeString' | 'dateString' | 'dateStringDash' | 'dateStringDot' | 'string' | 'unknown'}
 */
export function detectInputType(value) {
  if (value === null || value === undefined) return 'empty';

  if (value instanceof Date) return 'date';

  if (typeof value === 'number') {
    if (value > 1e12) return 'timestamp'; // 1724505600000 gibi
    if (value > 10000000) return 'intDate'; // 20240824
    if (value <= 2359) return 'intTime'; // 1430
  }

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return 'isoDate';
    if (/^\d{2}:\d{2}$/.test(value)) return 'timeString';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return 'dateString';
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return 'dateStringDash'; // MM-DD-YYYY or DD-MM-YYYY
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return 'dateStringDot'; // DD.MM.YYYY or MM.DD.YYYY
    return 'string';
  }

  return 'unknown';
}

/**
 * Converts input value to timestamp.
 *
 * @param {*} value - Input value to convert to timestamp.
 *
 * @returns {number | null} Timestamp in milliseconds, or null if invalid.
 */
export function toTimestamp(value) {
  const type = detectInputType(value);

  switch (type) {
    case 'date':
      return value.getTime();
    case 'timestamp':
      return value;
    case 'intDate': {
      // Parse as local time to avoid timezone shift issues
      // e.g., 20260102 -> year=2026, month=01, day=02
      const str = String(value);
      const year = parseInt(str.slice(0, 4), 10);
      const month = parseInt(str.slice(4, 6), 10) - 1; // months are 0-indexed
      const day = parseInt(str.slice(6, 8), 10);
      return new Date(year, month, day).getTime();
    }
    case 'isoDate': {
      // Parse as local time to avoid timezone shift issues
      // ISO dates without time (e.g., '2026-01-02') are interpreted as UTC by default
      // which causes day shift in timezones behind UTC
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        // If there's a time component, use the full ISO string
        if (value.includes('T')) {
          return new Date(value).getTime();
        }
        // Date-only: parse as local time
        return new Date(year, month, day).getTime();
      }
      return new Date(value).getTime();
    }
    case 'dateString': {
      // Handle MM/DD/YYYY or DD/MM/YYYY format with slashes
      const [a, b, year] = value.split('/').map(Number);
      const date = buildLocalDate(a, b, year);
      return date ? date.getTime() : null;
    }
    case 'dateStringDash': {
      // Handle MM-DD-YYYY or DD-MM-YYYY format with dashes
      const [a, b, year] = value.split('-').map(Number);
      const date = buildLocalDate(a, b, year);
      return date ? date.getTime() : null;
    }
    case 'dateStringDot': {
      // Handle DD.MM.YYYY or MM.DD.YYYY format with dots
      const [a, b, year] = value.split('.').map(Number);
      const date = buildLocalDate(a, b, year);
      return date ? date.getTime() : null;
    }
    case 'intTime': {
      const hours = Math.floor(value / 100);
      const minutes = value % 100;
      const today = new Date();
      today.setHours(hours, minutes, 0, 0);
      return today.getTime();
    }
    default:
      return null;
  }
}

/**
 * Checks if the input value is a valid date.
 *
 * @param {*} value - Input value to validate.
 *
 * @returns {boolean} True if the input value is a valid date, false otherwise.
 */
export function isValidDate(value) {
  return toTimestamp(value) !== null;
}

/**
 * Formats a time value into a short localized time string.
 *
 * @param {*} input - Input value to format.
 * @param {string} [emptyValue='-'] - Fallback value when input is invalid.
 *
 * @returns {string} Short localized time string.
 */
export function shortTime(input, emptyValue = '-') {
  const type = detectInputType(input);

  switch (type) {
    case 'intTime':
      return input === 0 ? emptyValue : intToTimeStr(input);
    case 'timeString':
      return input;
    case 'date':
    case 'timestamp': {
      const dateObj = new Date(toTimestamp(input));
      return format(dateObj, getLocalFormat({ type: 'time' }));
    }
    default:
      return emptyValue;
  }
}

/**
 * Formats a datetime value as a short localized datetime string.
 *
 * @param {*} input - Date/time value
 * @param {string} [emptyValue='-'] - Value returned if input is invalid
 *
 * @returns {string} Formatted date-time string or emptyValue
 */
export function shortDateTime(input, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  const dateObj = new Date(timestamp);

  return format(dateObj, getLocalFormat({ type: 'dateTime' }));
}

/**
 * Formats a date value into a short localized date string.
 *
 * @param {*} input - Input value to format.
 * @param {string} [emptyValue='-'] - Fallback value when input is invalid.
 *
 * @returns {string} Short localized date string.
 */
export function shortDate(input, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  const dateObj = new Date(timestamp);

  return format(dateObj, getLocalFormat({ type: 'date' }));
}

/**
 * AG Grid date-filter comparator for columns whose cell values are produced by
 * shortDate() (or any input toTimestamp understands). Parses the cell value with
 * the same locale rules used for formatting (isUsDateFormat), so the filter stays
 * consistent with the displayed value regardless of browser locale.
 *
 * @param {Date} filterLocalDateAtMidnight - Date picked in the filter, at local midnight.
 * @param {*} cellValue - Cell value (localized date string, int date, Date, etc.).
 *
 * @returns {number} 0 if same day, -1 if cell is before the filter date (or unparseable), 1 if after.
 */
export function dateFilterComparator(filterLocalDateAtMidnight, cellValue) {
  const timestamp = toTimestamp(cellValue);
  if (timestamp === null) return -1;

  const cellDate = new Date(timestamp);
  cellDate.setHours(0, 0, 0, 0);

  return Math.sign(cellDate.getTime() - filterLocalDateAtMidnight.getTime());
}

/**
 * AG Grid column sort comparator for date values formatted as localized strings,
 * so dates order chronologically instead of lexicographically.
 * Unparseable values (e.g. '-') sort as the epoch, grouping together.
 *
 * @param {*} valueA - First cell value.
 * @param {*} valueB - Second cell value.
 *
 * @returns {number} Negative if valueA is earlier, positive if later, 0 if equal.
 */
export function dateSortComparator(valueA, valueB) {
  return (toTimestamp(valueA) ?? 0) - (toTimestamp(valueB) ?? 0);
}

/**
 * As dateSortComparator, but empty or unparseable values (shown as '-') go last in both sort directions. AG Grid
 * passes `isInverted` (descending) and negates the result itself, so the empty side is flipped back here.
 *
 * @param {*} valueA - First cell value.
 * @param {*} valueB - Second cell value.
 * @param {*} [nodeA] - Unused (AG Grid row node).
 * @param {*} [nodeB] - Unused (AG Grid row node).
 * @param {boolean} [isInverted=false] - True when AG Grid sorts descending.
 *
 * @returns {number} Negative if valueA comes first in ascending order.
 */
export function dateSortComparatorNullsLast(valueA, valueB, nodeA, nodeB, isInverted = false) {
  const a = toTimestamp(valueA);
  const b = toTimestamp(valueB);
  if (a === null && b === null) return 0;
  if (a === null) return isInverted ? -1 : 1;
  if (b === null) return isInverted ? 1 : -1;
  return a - b;
}

/**
 * Formats a date value into a long localized date string.
 * US format: "February 23, 2026"
 * TR format: "23 Şubat 2026"
 *
 * @param {*} input - Input value to format (string, Date, number, etc.).
 * @param {string} [emptyValue='-'] - Fallback value when input is invalid.
 *
 * @returns {string} Long localized date string.
 */
export function longDate(input, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  const dateObj = new Date(timestamp);
  const formatString = isUsDateFormat() ? 'MMMM dd, yyyy' : 'dd MMMM yyyy';

  return format(dateObj, formatString);
}

/**
 * Formats a datetime value as a short localized datetime string in a specific timezone.
 *
 * @param {*} input - Date/time value
 * @param {string} timezone - IANA timezone (e.g. 'America/Los_Angeles')
 * @param {string} [emptyValue='-'] - Value returned if input is invalid
 * @returns {string} Formatted date-time string or emptyValue
 */
export function shortDateTimeInTimezone(input, timezone, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  return formatInTimeZone(timestamp, timezone || 'UTC', getLocalFormat({ type: 'dateTime' }));
}

/**
 * Formats a date value into a short localized date string in a specific timezone.
 *
 * @param {*} input - Date value
 * @param {string} timezone - IANA timezone
 * @param {string} [emptyValue='-'] - Value returned if input is invalid
 * @returns {string} Formatted date string or emptyValue
 */
export function shortDateInTimezone(input, timezone, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  return formatInTimeZone(timestamp, timezone || 'UTC', getLocalFormat({ type: 'date' }));
}

/**
 * Formats a time value into a short localized time string in a specific timezone.
 *
 * @param {*} input - Time/date value
 * @param {string} timezone - IANA timezone
 * @param {string} [emptyValue='-'] - Value returned if input is invalid
 * @returns {string} Formatted time string or emptyValue
 */
export function shortTimeInTimezone(input, timezone, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  return formatInTimeZone(timestamp, timezone || 'UTC', getLocalFormat({ type: 'time' }));
}

/**
 * Formats a date value into a long localized date string in a specific timezone.
 *
 * @param {*} input - Date value
 * @param {string} timezone - IANA timezone
 * @param {string} [emptyValue='-'] - Value returned if input is invalid
 * @returns {string} Formatted long date string or emptyValue
 */
export function longDateInTimezone(input, timezone, emptyValue = '-') {
  const timestamp = toTimestamp(input);
  if (!timestamp) return emptyValue;

  const formatString = isUsDateFormat() ? 'MMMM dd, yyyy' : 'dd MMMM yyyy';
  return formatInTimeZone(timestamp, timezone || 'UTC', formatString);
}
